import { delay } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ScratchPadDataActionPrimaryService } from "../../../shared/cubiq-services/action/impl/ScratchPadDataActionPrimaryService";
import { ScratchPadDataPrimaryLookupService } from "../../../shared/cubiq-services/lookup/model/impl/ScratchPadDataPrimaryLookupService";
import {
  OrderDataActionPromiseService,
  CcyPairPromiseLookupService,
  ClientPromiseLookupService,
  COLUMN_ORDER_STATE,
  COLUMN_ORDER_ERRORS,
  OrderAcceptancePromiseService,
  OrderSalesApprovalRequestPromiseService,
  SystemConfigPromiseService,
  Roles,
  ActiveUser,
  ActiveUserProfilePromiseService,
  TraderGroupPromiseLookupService,
  SysFeatures,
  TimezoneLookupService,
  RateSubscription,
  RateSubscriptionPromiseService,
  ScratchPadDataLookupService,
  CURVE_NDF,
  CURVE_FWD,
  CURVE_SPT,
  MarkupPromiseService,
} from "shared/cubiq-services";
import { BfxBaseBlotterService } from "app/bfx-base-blotter/bfx-base-blotter.service";
import { ScratchPadTableDefinitionFactory } from "shared/cubiq-services";
import {
  LinkedOrderRequestImpl,
  ClientImpl,
  OrderState,
  AcceptanceAction,
  AcceptanceType,
  SalesApprovalAction,
  TradingGroupImpl,
  UserType,
  ByteBuffer,
  FixingOrderRequestImpl,
  SavedOrderEventListImpl,
  RequestOrderType,
  OrderType,
  moment,
  TimeZoneImpl,
  Product,
  StandardDateImpl,
  CurrencyPair,
  BigNumber,
  MarkUpType,
} from "shared/cubiq-model-multi-asset";
import {
  OEToleranceService,
  EntryToleranceErrorsInfo,
} from "app/order-entry/services/basic/oe-tolerance.service";
import { OEDataModel } from "app/order-entry/data/models/oe-data.model";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { COLUMN_EXTERNAL_REF } from "shared/cubiq-services/model/ScratchPadTableDefinitionFactory.constants";

import {
  COLUMN_ORDER_ID,
  COLUMN_DESK_ID,
  COLUMN_SALES_GROUP_ID,
  COLUMN_ENTRY_USER_ID,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OEApiService } from "app/order-entry/services/oe-api.service";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { OECoordinatorService } from "app/order-entry/services/oe-coordinator.service";
import { ScratchPadOrder } from "../../../shared/cubiq-services/model/orders/ScratchPadOrder";
import { LoggerService } from "@bfx/logger";
import { ScratchPadOrderFactoryService } from "shared/cubiq-services/model/orders/ScratchPadOrderFactoryService";
const logger = LoggerService.for(__dirname);

export const PREFIX_SEARCH: string = "as_fv_";
export const PREFIX_SORT: string = "as_sort_";
export const FILE_EXTENSIONS: Array<string> = ["xlsx", "csv"];

const CONFIG_DEFAULT_TRADER_GROUP: string = "DEFAULT_TRADER_GROUP_ID";
const DEFAULT_TRADER_GROUP: string = "-1";

@Injectable()
export class BfxScratchPadService extends BfxBaseBlotterService {
  public pendingOrderRef: any = {};

  public fatFingersChecks: any = {};
  public negativeMarkupChecks: any = {};
  public rejectedOrders: any = {};

  public canApproveAsTrader: boolean = false;
  public canRejectAsTrader: boolean = false;
  public canApproveUnrestrictedAsTrader: boolean = false;
  public canApproveAsSales: boolean = false;
  public canCancelOrder: boolean = false;
  public canRejectAsSales: boolean = false;
  public canClearAsSales: boolean = false;
  public canApproveUnrestrictedAsSales: boolean = false;
  public bmAllowFutureFixingDate: boolean = false;
  public activeUser: ActiveUser = null;
  public traderGroups: TradingGroupImpl[] = null;
  private importErrorMessages: Array<string> = [];

  // Observable string sources
  private scratchPadActionsSubscription = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.scratchPadActionsSubscription.asObservable();

  // footer message
  private footerMsgEventSource = new Subject<any>();
  public footerCtrl$ = this.footerMsgEventSource.asObservable();

  private subjectUpdateEntry: Subject<any> = new Subject<any>();
  public partialUpdateSubject: Subject<Array<any>> = new Subject<Array<any>>();
  private defaultTraderGroupId: string = null;

  constructor(
    public dataService: ScratchPadDataPrimaryLookupService,
    public actionService: ScratchPadDataActionPrimaryService,
    public tableDefinitionService: ScratchPadTableDefinitionFactory,
    public orderDataService: OrderDataActionPromiseService,
    public ccyPairLookup: CcyPairPromiseLookupService,
    public clientLookupService: ClientPromiseLookupService,
    public coordinator: OECoordinatorService,
    public toleranceService: OEToleranceService,
    public orderDataActionService: OrderAcceptancePromiseService,
    public salesApproval: OrderSalesApprovalRequestPromiseService,
    public systemConfigService: SystemConfigPromiseService,
    public activeUserService: ActiveUserProfilePromiseService,
    public traderGroupService: TraderGroupPromiseLookupService,
    public oeApi: OEApiService,
    public layoutService: GoldenLayoutService,
    public translateService: TranslateService,
    public timeZoneLookup: TimezoneLookupService,
    public rateSubscriptionService: RateSubscriptionPromiseService,
    public markupService: MarkupPromiseService,
    private orderFactory: ScratchPadOrderFactoryService,
  ) {
    super(dataService, tableDefinitionService);
    this.initialize();
  }

  protected initialize(): void {
    this.systemConfigService.getSystemConfig(CONFIG_DEFAULT_TRADER_GROUP).then((value: string) => {
      this.defaultTraderGroupId = value != null ? value : DEFAULT_TRADER_GROUP;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT).then((value) => {
      this.canApproveAsTrader = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT).then((value) => {
      this.canRejectAsTrader = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED).then((value) => {
      this.canApproveUnrestrictedAsTrader = value;
    });

    this.systemConfigService.hasRole(Roles.DI_SALES_APPROVE).then((value) => {
      this.canApproveAsSales = value;
    });

    this.systemConfigService.hasRole(Roles.DI_SALES_REJECT).then((value) => {
      this.canRejectAsSales = value;
    });

    this.systemConfigService.hasRole(Roles.DI_SALES_CLEAR).then((value) => {
      this.canClearAsSales = value;
    });

    this.systemConfigService.hasRole(Roles.DI_SALES_UNRESTRICTED).then((value) => {
      this.canApproveUnrestrictedAsSales = value;
    });

    this.systemConfigService.getSystemConfig(SysFeatures.bmFixingDateEnabled).then((value) => {
      this.bmAllowFutureFixingDate = "Y" === value;
    });

    this.activeUserService.getActiveUserData().then((value: ActiveUser) => {
      this.activeUser = value;
    });

    this.traderGroupService.getAll().then((value: TradingGroupImpl[]) => {
      this.traderGroups = value;
    });

    this.subjectUpdateEntry.pipe(delay(300)).subscribe(this.onUpdateReference.bind(this));
  }

  protected onUpdateReference(references: any): void {
    this.oeApi.updateModelReference(references.oldReference, references.newReference);
  }

  protected onUpdateEvent(event: any): void {
    let rawRows: any;
    let processedArray: Array<any> = [];
    let keys: string[] = Object.keys(this._rawData);

    if (Array.isArray(event) || event instanceof SavedOrderEventListImpl) {
      if (event instanceof SavedOrderEventListImpl) {
        if (
          event.orderDataRequests &&
          event.orderDataRequests != null &&
          event.orderDataRequests &&
          event.orderDataRequests != null
        ) {
          let limitArr: any = event.orderDataRequests;
          let bmarksArray: any = event.fixingOrderRequests;
          rawRows = limitArr.concat(bmarksArray);
        } else
          rawRows = event.orderDataRequests ? event.orderDataRequests : event.fixingOrderRequests;
      } else rawRows = event;
      rawRows.forEach((row) => {
        const processed = this.processSingleUpdateEvent(row, keys);
        if (processed) {
          processedArray.push(processed);
        }
      });
      if (processedArray && processedArray.length > 0) this.onFullUpdateEvent(processedArray);
    } else {
      this.onFullUpdateEvent([this.processSingleUpdateEvent(event, keys)]);
    }
  }

  private processSingleUpdateEvent(rawRow: any, keys: string[]): any {
    if (!(rawRow instanceof ScratchPadOrder)) {
      rawRow = this.orderFactory.createScratchPadOrder(rawRow);
      this.processActivationExpirationTimes(rawRow);
    }

    if (rawRow != null && rawRow.externalClientReference != null) {
      delete rawRow.isProcessing;
      delete this.pendingOrderRef[rawRow.externalClientReference];
    }

    const oldReference: string = rawRow.oldClOrdId;
    if (oldReference != null) {
      delete this.pendingOrderRef[oldReference];

      let index: number = keys.indexOf(oldReference);

      let newReference: string = rawRow[COLUMN_EXTERNAL_REF];
      let oldSiblingRefId: string = rawRow["oldSiblingRefId"];

      if (oldReference != null && newReference != null && oldReference != newReference) {
        this.subjectUpdateEntry.next({
          oldReference: oldReference,
          newReference: newReference,
        });
        if (oldSiblingRefId != null) this.dataService.deleteSibling(oldSiblingRefId);
      }

      if (index >= 0) {
        keys[index] = rawRow[COLUMN_EXTERNAL_REF];
        let newRowData: any = {};
        keys
          .map((key: string) => (key == rawRow[COLUMN_EXTERNAL_REF] ? rawRow : this._rawData[key]))
          .forEach((row: any) => (newRowData[row[COLUMN_EXTERNAL_REF]] = row));
        this._rawData = newRowData;
      }
    }
    return rawRow;
  }

  public canAccept(row: any): boolean {
    let traderGroupId: string = row[COLUMN_DESK_ID];

    let isTrader: boolean = UserType.TRADER === this.activeUser.userTypeId;
    let isInGroup: boolean =
      traderGroupId != null && this.activeUser.tradingGroupsIdSet.has(traderGroupId);
    let isTraderAndUnrestricted: boolean = isTrader && this.canApproveUnrestrictedAsTrader;
    let isDefaultTraderGroup: boolean =
      traderGroupId != null && this.defaultTraderGroupId === traderGroupId.toString();

    let orderState: OrderState = row[COLUMN_ORDER_STATE];
    let traderAcceptanceRequired: boolean =
      orderState == OrderState.ACCEPTANCE_NEEDED ||
      orderState == OrderState.MODIFY_ACCEPTANCE_NEEDED;

    return (
      !isDefaultTraderGroup &&
      traderAcceptanceRequired &&
      ((isTrader && this.canApproveAsTrader && isInGroup) || isTraderAndUnrestricted)
    );
  }

  public canApprove(row: any): boolean {
    let salesGroupId: string = row[COLUMN_SALES_GROUP_ID];

    let isInSales: boolean =
      salesGroupId != null && this.activeUser.salesGroupsIdSet.has(salesGroupId);

    let isSales: boolean = UserType.SALES === this.activeUser.userTypeId;
    let salesApprovalRequired: boolean = row[COLUMN_ORDER_STATE] == OrderState.DI_NEEDED;

    return (
      salesApprovalRequired &&
      ((isSales && isInSales && this.canApproveAsSales) || this.canApproveUnrestrictedAsSales)
    );
  }

  public canReject(row: any): boolean {
    let traderGroupId: string = row[COLUMN_DESK_ID];
    let salesGroupId: string = row[COLUMN_SALES_GROUP_ID];

    let isTrader: boolean = UserType.TRADER === this.activeUser.userTypeId;
    let isInGroup: boolean =
      traderGroupId != null && this.activeUser.tradingGroupsIdSet.has(traderGroupId);
    let isTraderAndUnrestricted: boolean = isTrader && this.canApproveUnrestrictedAsTrader;
    let isDefaultTraderGroup: boolean =
      traderGroupId != null && this.defaultTraderGroupId === traderGroupId.toString();

    let isInSales: boolean =
      salesGroupId != null && this.activeUser.salesGroupsIdSet.has(salesGroupId);

    let orderState: OrderState = row[COLUMN_ORDER_STATE];
    let isSales: boolean = UserType.SALES === this.activeUser.userTypeId;
    let salesApprovalRequired: boolean = orderState == OrderState.DI_NEEDED;
    let traderAcceptanceRequired: boolean =
      orderState == OrderState.ACCEPTANCE_NEEDED ||
      orderState == OrderState.MODIFY_ACCEPTANCE_NEEDED;

    return (
      (salesApprovalRequired &&
        ((isSales && isInSales && this.canRejectAsSales) || this.canApproveUnrestrictedAsSales)) ||
      (!isDefaultTraderGroup &&
        traderAcceptanceRequired &&
        ((isTrader && this.canRejectAsTrader && isInGroup) || isTraderAndUnrestricted))
    );
  }

  public canAbort(row: any): boolean {
    let isEntryUser: boolean = row[COLUMN_ENTRY_USER_ID] == this.activeUser.userId;
    return isEntryUser;
  }

  public isPending(refId: string): boolean {
    return this.pendingOrderRef[refId] == true;
  }

  public isFailedFatFingers(refId: string): boolean {
    return this.fatFingersChecks[refId] != null;
  }

  public isNegativeMarkup(refId: string): boolean {
    return this.negativeMarkupChecks[refId] != null;
  }

  public isRejected(refId: string): boolean {
    return this.rejectedOrders[refId] != null;
  }

  public cloneOrderRequests(orderIds: Array<string>): Promise<any[]> {
    return this.dataService.cloneItems(orderIds);
  }

  public saveItems(extReferences: string[]): Promise<any> {
    this.updatePending(extReferences);
    return this.actionService.saveOrders(extReferences);
  }

  public deleteOrders(orderIds: string[]): Promise<void> {
    this.updatePending(orderIds);
    orderIds.forEach(this.closeOrderEntryScreen.bind(this));
    return this.removePending(orderIds, this.actionService.deleteOrders(orderIds));
  }

  public unbulkOrders(orderIds: string[]): Promise<FixingOrderRequestImpl[]> {
    orderIds.forEach(this.closeOrderEntryScreen.bind(this));
    return this.actionService.unbulkBenchmarkOrder(orderIds);
  }

  public bulkOrders(orderIds: string[]): Promise<FixingOrderRequestImpl[]> {
    orderIds.forEach(this.closeOrderEntryScreen.bind(this));
    return this.actionService.bulkBenchmarkOrders(orderIds);
  }

  public addOrUpdateRow(row: any): any {
    if (row["siblingOrderId"] == null || row["siblingOrderId"].length == 0) {
      if (!(row instanceof ScratchPadOrder)) {
        const scratchpadOrder = this.orderFactory.createScratchPadOrder(row);
        this.processActivationExpirationTimes(scratchpadOrder);
        return super.addOrUpdateRow(scratchpadOrder) ?? scratchpadOrder;
      } else {
        super.addOrUpdateRow(row);
      }
    }
  }

  public deleteRow(rowId: string): void {
    delete this._rawData[rowId];
  }

  public edit(rowId: string): void {
    let row: any = this._rawData[rowId];
    let orderId: string = row[COLUMN_EXTERNAL_REF];
    let orderEditMode: OENOperationMode = OENOperationMode.EDIT;
    let oeType: OENEntryType =
      row.requestOrderType === RequestOrderType.BMARK ? OENEntryType.BENCHMARK : OENEntryType.SPOT;

    if (row[COLUMN_ORDER_ID] != null && row[COLUMN_ORDER_ID] != "-") {
      orderId = row[COLUMN_ORDER_ID];
      orderEditMode =
        row.requestOrderType === RequestOrderType.BMARK
          ? OENOperationMode.MODIFY_SCRATCHPAD
          : OENOperationMode.MODIFY;
    }
    this.oeApi.doOrderEntryPopupForOrderId(orderId, orderEditMode, null, false, true);
  }

  protected prepareSubscriptions(orderRefs: string[]): Array<RateSubscription> {
    let lookup: ScratchPadDataLookupService = this.dataService.getBase();
    let subscriptionKeys: string[] = [];

    let orders: any[] = orderRefs.map((refId: string) => lookup.getItem(refId));
    let subscriptions: RateSubscription[] = [];

    orders
      .filter((a) => a instanceof LinkedOrderRequestImpl)
      .forEach((order: LinkedOrderRequestImpl) => {
        let curve: string =
          order.product === Product.NDF
            ? CURVE_NDF
            : order.product === Product.FWD
              ? CURVE_FWD
              : CURVE_SPT;
        let valueDate: Date = null;

        if (order.rootOrder.valueDate instanceof StandardDateImpl) {
          let dt: StandardDateImpl = order.rootOrder.valueDate;
          valueDate = new Date(dt.year, dt.month - 1, dt.day);
          const timezoneOffset = valueDate.getTimezoneOffset() * 60000;
          valueDate = new Date(valueDate.getTime() - timezoneOffset);
        }

        let ccyPair: CurrencyPair = this.ccyPairLookup
          .getBase()
          .getCcyPair(order.rootOrder.base, order.rootOrder.nonBase);

        if (this.toleranceService.shouldSubscribeBeforeCheck(ccyPair)) {
          let subscription: RateSubscription = new RateSubscription(
            ccyPair.base + ccyPair.nonBase,
            curve,
            order.rootOrder.tenor,
            valueDate,
          );
          let subscriptionKey: string = subscription.key;

          if (!subscriptionKeys.includes(subscriptionKey)) {
            subscriptions.push(subscription);
            subscriptionKeys.push(subscriptionKey);
          }
        }
      });

    return subscriptions;
  }

  public submitOrders(
    orderRefs: string[],
    skipFatFingersCheck: boolean = false,
    skipNegativeMarkupCheck: boolean = false,
  ): Promise<Array<any>> {
    this.updatePending(orderRefs);
    orderRefs.forEach(this.closeOrderEntryScreen.bind(this));

    return new Promise<any>(async (resolve, reject) => {
      let rateSubscriptions: RateSubscription[] = [];

      if (!skipFatFingersCheck) {
        rateSubscriptions = this.prepareSubscriptions(orderRefs);
      }

      let fwdSubscriptions: RateSubscription[] =
        await this.rateSubscriptionService.subscribeWithChecks(rateSubscriptions);

      Promise.all(
        orderRefs.map(async (orderRef: string) => {
          return new Promise<Array<any>>(async (res, rej) => {
            let model: OEDataModel = new OEDataModel();
            let value = await this.dataService.getItem(orderRef);
            let ccyPair: CurrencyPair;
            let precision;
            let isInvert;
            let result = [];

            if (!skipNegativeMarkupCheck) {
              let errors = [];
              let markup: BigNumber;
              let markupType: MarkUpType;
              let markupTypeLabel = "";
              if (value instanceof FixingOrderRequestImpl) {
                if (value?.fixingOrderData?.markup != null) {
                  markup = new BigNumber(value?.fixingOrderData?.markup);
                }
                markupType = value?.fixingOrderData.markupType;
                ccyPair = this.ccyPairLookup
                  .getBase()
                  .getCcyPair(value.fixingOrderData.base, value.fixingOrderData.nonBase);
                isInvert = ccyPair.base === value.fixingOrderData.nonBase;
              } else if (value?.rootOrder?.markup != null) {
                if (value?.rootOrder?.markup != null) {
                  markup = new BigNumber(value?.rootOrder?.markup);
                }
                markupType = value?.rootOrder.markupType;
                ccyPair = this.ccyPairLookup
                  .getBase()
                  .getCcyPair(value.rootOrder.base, value.rootOrder.nonBase);
                isInvert = ccyPair.base === value.rootOrder.nonBase;
              }

              if (BigNumber.isBigNumber(markup) && markup.isLessThan(0)) {
                markupTypeLabel = this.markupService.getMarkupTypeLabel(markupType);
                precision = this.markupService.getMarkupPrecisionByType(
                  ccyPair,
                  isInvert,
                  markupType,
                );
                this.toleranceService.addError(errors, `order.entry.negative.markup.warning`, [
                  markup != null && markupTypeLabel != null
                    ? ` value of: ${markup.toFixed(precision)} ${markupTypeLabel}`
                    : ``,
                ]);
                this.updateNegativeMarginCheck(orderRef, errors);
              }

              result = [...result, ...errors];
            }

            if (!skipFatFingersCheck) {
              let errors = [];
              if (value instanceof FixingOrderRequestImpl) {
                model.bmarkRequestModel = value;
                errors = this.toleranceService.checkEntryAmountToleranceErrors(model);
                this.updateFatFingersCheck(orderRef, errors);
              } else {
                model.requestModel = value;
                let client: ClientImpl = await this.clientLookupService.getClient(
                  value.rootOrder?.clientCode,
                );
                model.client = client;
                let errors = await this.toleranceService.checkEntryToleranceErrors(model, true);
                if (errors.length == 0) {
                  errors = this.toleranceService.checkEntryAmountToleranceErrors(model);
                }
                this.updateFatFingersCheck(orderRef, errors);
              }
              result = [...result, ...errors];
            }
            res(result);
          });
        }),
      )
        .then(() => {
          let ordersToSubmit: Array<string> = orderRefs.filter(
            (orderRef: string) =>
              !this.isNegativeMarkup(orderRef) && !this.isFailedFatFingers(orderRef),
          );
          orderRefs.forEach(
            ((ref: string) => {
              delete this.pendingOrderRef[ref];
            }).bind(this),
          );
          if (ordersToSubmit.length > 0) {
            this.actionService
              .submitBulkedWithConfigOrders(ordersToSubmit)
              .then((value: Array<any>) => {
                // NEEDS REVIEW WE'RE GETTING A BulkOrderLifeCycleEventImpl
                // value.forEach((val: any, index: number) => {
                //   if (val['enType'] == 'com.bcdfx.broker.ErrorsImpl') {
                //     let orderIdsSet: Set<string> = new Set<string>();
                //     orderIdsSet.add(ordersToSubmit[index]);
                //     let row: any = this.getRowDataForIds(orderIdsSet)[0];
                //     row[COLUMN_ORDER_STATE] = OrderState.REJECTED;
                //     // row[PREFIX_SEARCH + COLUMN_ORDER_STATE] = row[PREFIX_SORT + COLUMN_ORDER_STATE] = OrderState[OrderState.REJECTED];
                //     if (val.errorMessages != null && val.errorMessages.length > 0) {
                //       row[COLUMN_ORDER_ERRORS] = this.translateError(val.errorMessages[0]);
                //     }
                //   }
                // });
                if (this.bmAllowFutureFixingDate) {
                  resolve(value);
                } else {
                  resolve([]);
                }
              })
              .catch((error) => reject(error));
          } else {
            resolve([]);
          }
        })
        .finally(() => {
          try {
            if (fwdSubscriptions != null && fwdSubscriptions.length > 0) {
              this.rateSubscriptionService.unsubscribeWithChecks(rateSubscriptions);
            }
          } catch (e) {
            logger.out.error("BfxScratchPadService: Failed to clean up rate subscriptions: " + e);
          }
        });
    });
  }

  public getRowData(): Array<any> {
    let rowsData: Array<any> = super.getRowData();
    let siblingMap: any = {};

    rowsData.forEach((rowData: any) => {
      let refId: string = rowData[COLUMN_EXTERNAL_REF];

      let siblingId: string = rowData["siblingId"];

      let siblingOrderId: string = siblingMap[refId];
      if (
        siblingOrderId != null &&
        rowData[COLUMN_ORDER_ID] != null &&
        rowData[COLUMN_ORDER_ID].length > 0 &&
        siblingOrderId != null &&
        siblingOrderId.length > 0
      ) {
        if (parseInt(siblingOrderId) < parseInt(rowData[COLUMN_ORDER_ID])) {
          delete siblingMap[siblingId];
          siblingMap[refId] = rowData[COLUMN_ORDER_ID];
        } else {
          delete siblingMap[refId];
          siblingMap[siblingId] = siblingOrderId;
        }
      } else if (siblingId != null) {
        siblingMap[siblingId] = rowData[COLUMN_ORDER_ID];
      }

      if (this.isPending(refId)) {
        rowData.isPending = true;
      } else if (this.isRejected(refId)) {
        rowData[COLUMN_ORDER_STATE] = OrderState.REJECTED;

        if (rowData[COLUMN_ORDER_ERRORS]) rowData[COLUMN_ORDER_ERRORS] = "";
      } else if (this.isNegativeMarkup(refId)) {
        let errors: Array<any> = this.negativeMarkupChecks[refId];
        rowData[COLUMN_ORDER_ERRORS] = this.translateError(errors[0]);
      } else if (this.isFailedFatFingers(refId)) {
        let errors: Array<any> = this.fatFingersChecks[refId];
        rowData[COLUMN_ORDER_ERRORS] = this.translateError(errors[0]);
      }

      rowData["icon"] = this.getIconData(rowData);
    });

    rowsData = rowsData.filter((rowData: any) => siblingMap[rowData[COLUMN_EXTERNAL_REF]] == null);
    return rowsData;
  }

  public accept(orderIds: string[]): Promise<any> {
    let acceptingNegativeMarkup = false;

    if (orderIds.some((orderIds) => this.isNegativeMarkup(orderIds))) {
      this.updatePending(orderIds, true, true);
      acceptingNegativeMarkup = true;
    } else {
      this.updatePending(orderIds, true, true);
    }

    let limitAcceptanceNeeded: string[] = [];
    let benchmarkAcceptanceNeeded: string[] = [];
    let limitModifyAcceptanceNeeded: string[] = [];
    let benchmarkModifyAcceptanceNeeded: string[] = [];
    let diNeeded: string[] = [];
    let submitOrders: string[] = [];

    // Filter out the failed fat finger checks
    orderIds = orderIds.filter((orderId: string) => {
      let checkFailed: boolean = this.isNegativeMarkup(orderId) || this.isFailedFatFingers(orderId);

      if (checkFailed && acceptingNegativeMarkup) {
        delete this.negativeMarkupChecks[orderId];
        submitOrders.push(orderId);
      } else if (checkFailed) {
        delete this.fatFingersChecks[orderId];
        submitOrders.push(orderId);
      }

      // Continue if check failed
      return !checkFailed;
    });

    let refs: Set<string> = new Set<string>(orderIds);
    let rows: ScratchPadOrder[] = this.getRowDataForIds(refs);

    rows.forEach((row: ScratchPadOrder) => {
      let orderIdArray: string[] = null;

      if (row[COLUMN_ORDER_STATE] == OrderState.ACCEPTANCE_NEEDED) {
        orderIdArray =
          row.requestOrderType == RequestOrderType.BMARK
            ? benchmarkAcceptanceNeeded
            : limitAcceptanceNeeded;
      } else if (row[COLUMN_ORDER_STATE] == OrderState.MODIFY_ACCEPTANCE_NEEDED) {
        orderIdArray =
          row.requestOrderType == RequestOrderType.BMARK
            ? benchmarkModifyAcceptanceNeeded
            : limitModifyAcceptanceNeeded;
      } else if (row[COLUMN_ORDER_STATE] == OrderState.DI_NEEDED) {
        orderIdArray = diNeeded;
      }

      if (orderIdArray != null) {
        orderIdArray.push(row.orderId);
      }
    });

    return new Promise<void>((resolve) => {
      if (limitAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting acceptance for " +
              limitAcceptanceNeeded.length +
              " limit orders.",
          );
          this.orderDataActionService.requestAcceptance(
            limitAcceptanceNeeded,
            "",
            AcceptanceAction.ACCEPT,
            AcceptanceType.ENTRY,
            null,
            null,
            null,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to request acceptance for " +
              limitAcceptanceNeeded.length +
              " limit orders.",
          );
        }
      }

      if (benchmarkAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting acceptance for " +
              benchmarkAcceptanceNeeded.length +
              " benchmark orders.",
          );
          this.orderDataActionService.requestAcceptance(
            benchmarkAcceptanceNeeded,
            "",
            AcceptanceAction.ACCEPT,
            AcceptanceType.ENTRY,
            null,
            null,
            OrderType.BENCHMARK,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to request acceptance for " +
              benchmarkAcceptanceNeeded.length +
              " benchmark orders.",
          );
        }
      }

      if (limitModifyAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting modify acceptance for " +
              limitModifyAcceptanceNeeded.length +
              " limit orders.",
          );
          this.orderDataActionService.requestAcceptance(
            limitModifyAcceptanceNeeded,
            "",
            AcceptanceAction.ACCEPT,
            AcceptanceType.MODIFICATION,
            null,
            null,
            null,
          );
        } catch (error) {
          logger.out.warn(
            ": Failed to request modify acceptance for " +
              this.constructor.name +
              limitModifyAcceptanceNeeded.length +
              " limit orders.",
          );
        }
      }

      if (benchmarkModifyAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting modify acceptance for " +
              benchmarkModifyAcceptanceNeeded.length +
              " benchmark orders.",
          );
          this.orderDataActionService.requestAcceptance(
            benchmarkModifyAcceptanceNeeded,
            "",
            AcceptanceAction.ACCEPT,
            AcceptanceType.MODIFICATION,
            null,
            null,
            OrderType.BENCHMARK,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to request modify acceptance for " +
              benchmarkModifyAcceptanceNeeded.length +
              " benchmark orders.",
          );
        }
      }

      if (diNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name + ": Requesting DI approval for " + diNeeded.length + " orders.",
          );
          this.salesApproval.requestApproval(diNeeded, "", SalesApprovalAction.APPROVE);
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to request DI approval for " +
              diNeeded.length +
              " orders.",
          );
        }
      }

      if (submitOrders.length > 0) {
        try {
          logger.out.info(
            this.constructor.name + ": Submitting " + submitOrders.length + " orders.",
          );
          this.submitOrders(submitOrders, !acceptingNegativeMarkup, true);
        } catch (error) {
          logger.out.warn(
            this.constructor.name + ": Failed to submit " + submitOrders.length + " orders.",
          );
        }
      }

      resolve();
    });
  }

  public reject(orderIds: string[], abort: boolean = false): Promise<any> {
    this.updatePending(orderIds, true, true);

    let acceptanceAction: AcceptanceAction = abort
      ? AcceptanceAction.ABORT
      : AcceptanceAction.REJECT;
    let acceptanceActionTitle: string = abort ? "abort" : "reject";

    let limitAcceptanceNeeded: string[] = [];
    let benchmarkAcceptanceNeeded: string[] = [];
    let limitModifyAcceptanceNeeded: string[] = [];
    let benchmarkModifyAcceptanceNeeded: string[] = [];
    let diNeeded: string[] = [];

    let refs: Set<string> = new Set<string>(orderIds);
    let rows: ScratchPadOrder[] = this.getRowDataForIds(refs);

    // Filter out the failed fat finger checks
    rows = rows.filter((row: ScratchPadOrder) => {
      let orderId: string = row.externalClientReference;
      let checkFailed: boolean = this.isNegativeMarkup(orderId) || this.isFailedFatFingers(orderId);

      if (checkFailed) {
        row.errors = this.negativeMarkupChecks[orderId] ?? this.fatFingersChecks[orderId];
        (row as any).orderState = OrderState.REJECTED;
        delete this.negativeMarkupChecks[orderId];
        delete this.fatFingersChecks[orderId];
        delete this.pendingOrderRef[orderId];
        delete row.isProcessing;
        this.rejectedOrders[orderId] = true;
      }

      // Continue if check failed
      return !checkFailed;
    });

    rows.forEach((row: ScratchPadOrder) => {
      let orderIdArray: string[] = null;

      if (row[COLUMN_ORDER_STATE] == OrderState.ACCEPTANCE_NEEDED) {
        orderIdArray =
          row.requestOrderType == RequestOrderType.BMARK
            ? benchmarkAcceptanceNeeded
            : limitAcceptanceNeeded;
      } else if (row[COLUMN_ORDER_STATE] == OrderState.MODIFY_ACCEPTANCE_NEEDED) {
        orderIdArray =
          row.requestOrderType == RequestOrderType.BMARK
            ? benchmarkModifyAcceptanceNeeded
            : limitModifyAcceptanceNeeded;
      } else if (row[COLUMN_ORDER_STATE] == OrderState.DI_NEEDED) {
        if (abort) {
          orderIdArray =
            row.requestOrderType == RequestOrderType.BMARK
              ? benchmarkAcceptanceNeeded
              : limitAcceptanceNeeded;
        } else {
          orderIdArray = diNeeded;
        }
      }

      if (orderIdArray != null) {
        orderIdArray.push(row.orderId);
      }
    });

    return new Promise<void>((resolve) => {
      if (limitAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting " +
              acceptanceActionTitle +
              " for " +
              limitAcceptanceNeeded.length +
              " limit orders.",
          );
          this.orderDataActionService.requestAcceptance(
            limitAcceptanceNeeded,
            "",
            acceptanceAction,
            AcceptanceType.ENTRY,
            null,
            null,
            null,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to " +
              acceptanceActionTitle +
              " " +
              limitAcceptanceNeeded.length +
              " limit orders.",
          );
        }
      }

      if (benchmarkAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting reject " +
              benchmarkAcceptanceNeeded.length +
              " benchmark orders.",
          );
          this.orderDataActionService.requestAcceptance(
            benchmarkAcceptanceNeeded,
            "",
            acceptanceAction,
            AcceptanceType.ENTRY,
            null,
            null,
            OrderType.BENCHMARK,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to " +
              acceptanceActionTitle +
              " " +
              benchmarkAcceptanceNeeded.length +
              " benchmark orders.",
          );
        }
      }

      if (limitModifyAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting modify " +
              acceptanceActionTitle +
              " for " +
              limitModifyAcceptanceNeeded.length +
              " limit orders.",
          );
          this.orderDataActionService.requestAcceptance(
            limitModifyAcceptanceNeeded,
            "",
            acceptanceAction,
            AcceptanceType.MODIFICATION,
            null,
            null,
            null,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to modify " +
              acceptanceActionTitle +
              " " +
              limitModifyAcceptanceNeeded.length +
              " limit orders.",
          );
        }
      }

      if (benchmarkModifyAcceptanceNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name +
              ": Requesting modify " +
              acceptanceActionTitle +
              " for " +
              benchmarkModifyAcceptanceNeeded.length +
              " benchmark orders.",
          );
          this.orderDataActionService.requestAcceptance(
            benchmarkModifyAcceptanceNeeded,
            "",
            acceptanceAction,
            AcceptanceType.MODIFICATION,
            null,
            null,
            OrderType.BENCHMARK,
          );
        } catch (error) {
          logger.out.warn(
            this.constructor.name +
              ": Failed to modify " +
              acceptanceActionTitle +
              " " +
              benchmarkModifyAcceptanceNeeded.length +
              " benchmark orders.",
          );
        }
      }

      if (diNeeded.length > 0) {
        try {
          logger.out.info(
            this.constructor.name + ": Requesting DI reject for " + diNeeded.length + " orders.",
          );
          this.salesApproval.requestApproval(diNeeded, "", SalesApprovalAction.REJECT);
        } catch (error) {
          logger.out.warn(
            this.constructor.name + ": Failed to DI reject " + diNeeded.length + " orders.",
          );
        }
      }

      resolve();
    });
  }

  public closeOrderEntryScreen(orderRef: string): void {
    this.coordinator.cmdCloseOrderEntryScreen(orderRef);
  }

  public getIconData(rowData: any): string {
    let refId: string = rowData[COLUMN_EXTERNAL_REF];
    let response: string = "";
    let error: any = rowData[COLUMN_ORDER_ERRORS];

    if (this.isPending(refId) || rowData.isProcessing === true) {
      return "";
    }

    if (
      this.isNegativeMarkup(refId) &&
      Array.isArray(this.negativeMarkupChecks[refId]) &&
      this.negativeMarkupChecks[refId].length > 0
    ) {
      error = ` title="${this.translateError(this.negativeMarkupChecks[refId][0])}" `;
    } else if (
      this.isFailedFatFingers(refId) &&
      Array.isArray(this.fatFingersChecks[refId]) &&
      this.fatFingersChecks[refId].length > 0
    ) {
      const errors: Array<string> = this.fatFingersChecks[refId];
      const errorMessages: string = errors.map((err) => this.translateError(err)).join("\n");
      error = ' title="' + errorMessages + '" ';
    } else if (error != null && error.errorMessages != null && error.errorMessages.length > 0) {
      const errors: Array<string> = error.errorMessages;
      const errorMessages: string = errors.map((err) => this.translateError(err)).join("\n");
      error = ' title="' + errorMessages + '" ';
    } else if (error != null && error.length > 0) {
      error = ' title="' + this.translateService.instant(error) + '" ';
    }

    if (this.isNegativeMarkup(refId)) {
      response =
        '<i class="icon icon_ion_warning_filled bfx-scratchpad-tolerance"' + error + "></i>";
    } else if (this.isFailedFatFingers(refId)) {
      let errorInfo: EntryToleranceErrorsInfo = this.toleranceService.getEntryToleranceErrorsInfo(
        this.fatFingersChecks[refId],
      );
      if (errorInfo.isOutsideCcyPairEntryTolerance || errorInfo.isOutsideEntryAmtTolerance) {
        response =
          '<i class="icon icon_ion_warning_filled bfx-scratchpad-tolerance"' + error + "></i>";
      } else if (errorInfo.isWrongSideOfMarket) {
        response =
          '<i class="icon icon_ion_warning_filled bfx-scratchpad-tolerance-wom"' + error + "></i>";
      }
    } else {
      if (
        rowData[COLUMN_ORDER_STATE] === OrderState.ACCEPTANCE_NEEDED ||
        rowData[COLUMN_ORDER_STATE] === OrderState.DI_NEEDED ||
        rowData[COLUMN_ORDER_STATE] === OrderState.REJECTED
      ) {
        this.clearFooterMessage();
      }
    }

    return response;
  }

  private updateNegativeMarginCheck(ref: string, errors: any[]): boolean {
    delete this.pendingOrderRef[ref];

    if (errors.length > 0) {
      this.negativeMarkupChecks[ref] = errors;
      let orderIDs: Set<string> = new Set<string>();
      orderIDs.add(ref);
      //THIS NEEDS REVIEW
      let rows: Array<any> = this.getRowDataForIds(orderIDs);
      this.onUpdateEvent(rows);
    } else {
      delete this.negativeMarkupChecks[ref];
    }

    return errors.length > 0;
  }

  private updateFatFingersCheck(ref: string, errors: any[]): boolean {
    delete this.pendingOrderRef[ref];

    if (errors.length > 0) {
      this.fatFingersChecks[ref] = errors;
      let orderIDs: Set<string> = new Set<string>();
      orderIDs.add(ref);
      //THIS NEEDS REVIEW
      let rows: Array<any> = this.getRowDataForIds(orderIDs);
      this.onUpdateEvent(rows);
    } else {
      delete this.fatFingersChecks[ref];
    }

    return errors.length > 0;
  }

  private updatePending(
    refs: string[],
    skipFatFingersCheck: boolean = false,
    skipNegativeMarkupCheck: boolean = false,
  ): void {
    refs.forEach(
      ((ref: string) => {
        this.pendingOrderRef[ref] = true;
        delete this.rejectedOrders[ref];
        if (!skipNegativeMarkupCheck) {
          delete this.negativeMarkupChecks[ref];
        }
        if (!skipFatFingersCheck) {
          delete this.fatFingersChecks[ref];
        }
      }).bind(this),
    );
    let orderIDs: Set<string> = new Set<string>();
    refs.forEach((ref: string) => orderIDs.add(ref));
    let rows: Array<ScratchPadOrder> = this.getRowDataForIds(orderIDs).map(
      (row: ScratchPadOrder) => {
        row.isProcessing = true;
        return row;
      },
    );
    this.partialUpdateSubject.next(rows);
  }

  private removePending(refs: string[], promise: Promise<any>): Promise<any> {
    return new Promise<any>((res) => {
      promise.then((value: any) => {
        refs.forEach(
          ((ref: string) => {
            delete this.pendingOrderRef[ref];
          }).bind(this),
        );
        res(value);
      });
    });
  }

  private translateError(error: string): string {
    try {
      let err: any = JSON.parse(error);
      return this.translateService.instant(err.id, err.args);
    } catch (e) {
      logger.out.warn("Failed to translate error: ", error);
    }
  }

  public importOrdersFromFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      this.importErrorMessages = [];
      if (!this.validateFileType(file.name)) {
        this.importErrorMessages.push(
          this.translateService.instant("scratchpad.import.unsupported.file.type"),
        );
        this.showFailedDialog();
        reject("BfxScratchPadService --> unsuported file");
        return;
      }

      try {
        this.readUploadedFileAsArrayBuffer(file).then((fileContent: string | ArrayBuffer) => {
          this.actionService.importOrders(file.name, ByteBuffer.wrap(fileContent)).then((res) => {
            if (res && res.errorMessages && Array.isArray(res.errorMessages)) {
              res.errorMessages.forEach((err) => {
                this.importErrorMessages.push(this.translateError(err));
              });
              if (this.importErrorMessages.length > 0) {
                this.showFailedDialog();
                reject("BfxScratchPadService --> server validation errors");
              }
            }
            resolve();
          });
        });
      } catch (e) {
        reject("BfxScratchPadService --> " + e);
      }
    });
  }

  readUploadedFileAsArrayBuffer(inputFile): Promise<string | ArrayBuffer> {
    const fR = new FileReader();
    return new Promise((resolve, reject) => {
      fR.onerror = () => {
        this.importErrorMessages.push(
          this.translateService.instant("scratchpad.import.unsupported.file.format"),
        );
        fR.abort();
        reject("");
      };
      fR.onload = () => {
        resolve(fR.result);
      };
      fR.readAsArrayBuffer(inputFile);
    });
  }

  validateFileType(fileName: string): boolean {
    return (
      FILE_EXTENSIONS.find((ext) => {
        return ext === fileName.substring(fileName.lastIndexOf(".") + 1);
      }) !== undefined
    );
  }

  public showFailedDialog(message?: string) {
    if (this.importErrorMessages.length > 0 || message != null) {
      let payload = {
        messages: message != null ? [message] : this.importErrorMessages,
        title: this.translateService.instant("scratchpad.import.failed.title"),
        intent: "warning",
        callback: () => {},
        callbackNegative: () => {},
      };
      this.scratchPadActionsSubscription.next(
        new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK, null, null, payload),
      );
    }
  }

  public notifyImportBusyOnFooter(message: string): void {
    try {
      message = this.translateService.instant(message);
      this.notifyOnFooter({ message: message, duration: 120000 });
    } catch (ex) {
      logger.out.error(`EXCEPTION: BfxScratchPadService.notifyBusyOnFooter(): %O`, ex);
    }
  }

  public notifyImportCompleteOnFooter(): void {
    try {
      let message: string = null;
      message = this.translateService.instant("footer.notification.scratchpad.import.complete");
      this.notifyOnFooter(message);
    } catch (ex) {
      logger.out.error(`EXCEPTION: BfxScratchPadService.notifyImportSuccessOnFooter(): %O`, ex);
    }
  }

  public notifyImportFailureOnFooter(): void {
    try {
      let message: string = null;
      message = this.translateService.instant("footer.notification.scratchpad.import.failure");
      this.notifyOnFooter(message);
    } catch (ex) {
      logger.out.error(`EXCEPTION: BfxScratchPadService.notifyImportFailureOnFooter(): %O`, ex);
    }
  }

  public notifyOnFooter(message: any): void {
    this.footerMsgEventSource.next(message);
  }

  public clearFooterMessage() {
    try {
      let message: string = null;
      message = " ";
      this.notifyOnFooter(message);
    } catch (ex) {
      logger.out.error(`EXCEPTION: BfxScratchPadService.notifyImportFailureOnFooter(): %O`, ex);
    }
  }

  protected processActivationExpirationTimes(order: ScratchPadOrder): void {
    order.activationTimeUnix = this.getActivationTime(order);
    order.expiratonTimeUnix = this.getExpirationTime(order);
  }

  protected getActivationTime(order: ScratchPadOrder): number {
    if (order.activationDetails) {
      try {
        let actDate: string = order.activationDetails.activationDate;
        let actTime: string = order.activationDetails.activationTime;
        let timezone: string = order.activationDetails.timezone;

        if (actDate && actTime && timezone) {
          let tz: TimeZoneImpl = this.timeZoneLookup.getTimezone(timezone);
          return moment.tz(actDate + " " + actTime, "YYYY-MM-DD HH:mm", tz.javaTimeZoneCd).unix();
        }
      } catch (e) {
        logger.out.error(
          "Failed to parse activation date for order: " + order["orderId"] + ": " + e,
        );
      }
    }

    return 0;
  }

  protected getExpirationTime(order: ScratchPadOrder): number {
    if (order.expiryDetails) {
      try {
        let expDate: string = order.expiryDetails.expiryDate;
        let expTime: string = order.expiryDetails.expiryTime;
        let timezone: string = order.expiryDetails.timezone;

        let tmAls: string = order.expiryDetails.timeAlias;
        if (tmAls != undefined && tmAls === "DAY") return -1;

        if (expDate && expTime && timezone) {
          let tz: TimeZoneImpl = this.timeZoneLookup.getTimezone(timezone);
          return moment.tz(expDate + " " + expTime, "YYYY-MM-DD HH:mm", tz.javaTimeZoneCd).unix();
        }
      } catch (e) {
        logger.out.error(
          "Failed to parse expiration date for order: " + order["orderId"] + ": " + e,
        );
      }
    }

    return 0;
  }

  /**
   * Check all orders to see if they can be bulked
   * @param orders
   * @returns
   */
  public canBulkOrders(orders: ScratchPadOrder[]): boolean {
    return (
      orders.length > 1 &&
      orders.every(
        (order) =>
          // We cannot bulk non-BMs, multi-allocation orders or orders that have been saved
          order.orderType === OrderType.BENCHMARK &&
          order.multiAllocation === false &&
          order.orderId == null,
      )
    );
  }
}
